export const HOLOS_PRODUCTIVITY_KEY = "holos_productivity_v1"
export const HOLOS_ACTION_EVENT = "holos-productivity-action"

const text = (value, max = 500) => typeof value === "string" ? value.trim().slice(0, max) : ""
const id = (prefix) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
const bool = (value, fallback = false) => typeof value === "boolean" ? value : fallback
const days = (value) => Array.isArray(value) ? [...new Set(value.map(Number).filter((day) => day >= 0 && day <= 6))].slice(0, 7) : []
const time = (value) => /^\d{2}:\d{2}$/.test(value) ? value : "09:00"

export const PRODUCTIVITY_DEFAULTS = {
  version: 3,
  notes: [],
  shelf: [],
  clipboard: [],
  sessions: [],
  routines: [],
  macros: [],
  windowRules: [],
  automationRules: [],
  holosFiles: [],
  sharedBoards: [],
  appBlueprints: [],
  aiBudgets: [],
}

const normalizeWindows = (value) => Array.isArray(value) ? value.slice(0, 12).filter((item) => item && typeof item.typeId === "string").map((item) => ({
  typeId: item.typeId.slice(0, 64),
  x: Number(item.x) || 88,
  y: Number(item.y) || 60,
  w: Math.max(300, Number(item.w) || 860),
  h: Math.max(180, Number(item.h) || 560),
  minimized: !!item.minimized,
})) : []

export function normalizeProductivity(value) {
  const source = value && typeof value === "object" ? value : {}
  return {
    version: 3,
    notes: Array.isArray(source.notes) ? source.notes.slice(0, 80).map((item) => ({
      id: text(item?.id, 80) || id("note"),
      text: text(item?.text, 4000),
      pinned: !!item?.pinned,
      createdAt: Number(item?.createdAt) || Date.now(),
    })).filter((item) => item.text) : [],
    shelf: Array.isArray(source.shelf) ? source.shelf.slice(0, 60).map((item) => ({
      id: text(item?.id, 80) || id("shelf"),
      type: ["text", "url", "file"].includes(item?.type) ? item.type : "text",
      label: text(item?.label, 120),
      value: text(item?.value, 8000),
      preview: text(item?.preview, 3000),
      createdAt: Number(item?.createdAt) || Date.now(),
    })).filter((item) => item.value) : [],
    clipboard: Array.isArray(source.clipboard) ? source.clipboard.slice(0, 30).map((item) => ({
      id: text(item?.id, 80) || id("clip"),
      text: text(item?.text, 4000),
      createdAt: Number(item?.createdAt) || Date.now(),
    })).filter((item) => item.text) : [],
    sessions: Array.isArray(source.sessions) ? source.sessions.slice(0, 24).map((item) => ({
      id: text(item?.id, 80) || id("session"),
      name: text(item?.name, 80) || "Holos Sitzung",
      desktopId: text(item?.desktopId, 80),
      windows: normalizeWindows(item?.windows),
      schedule: {
        enabled: bool(item?.schedule?.enabled),
        time: time(item?.schedule?.time),
        days: days(item?.schedule?.days),
        lastRun: text(item?.schedule?.lastRun, 16),
      },
      createdAt: Number(item?.createdAt) || Date.now(),
    })) : [],
    routines: Array.isArray(source.routines) ? source.routines.slice(0, 24).map((item) => ({
      id: text(item?.id, 80) || id("routine"),
      name: text(item?.name, 80) || "Holos Routine",
      enabled: bool(item?.enabled, true),
      time: time(item?.time),
      days: days(item?.days),
      sessionId: text(item?.sessionId, 80),
      focus: bool(item?.focus),
      apps: Array.isArray(item?.apps) ? item.apps.filter((app) => typeof app === "string").map((app) => app.slice(0, 64)).slice(0, 8) : [],
      lastRun: text(item?.lastRun, 16),
    })) : [],
    macros: Array.isArray(source.macros) ? source.macros.slice(0, 24).map((item) => ({
      id: text(item?.id, 80) || id("macro"),
      name: text(item?.name, 80) || "Holos Makro",
      actions: Array.isArray(item?.actions) ? item.actions.slice(0, 30).filter((action) => action && ["open", "layout", "desktop", "focus"].includes(action.type)).map((action) => ({
        type: action.type,
        value: text(action.value, 120),
      })) : [],
    })) : [],
    windowRules: Array.isArray(source.windowRules) ? source.windowRules.slice(0, 24).map((item) => ({
      id: text(item?.id, 80) || id("rule"),
      name: text(item?.name, 80) || "Fensterregel",
      enabled: bool(item?.enabled, true),
      windowId: text(item?.windowId, 64),
      snap: ["", "l", "r", "full", "tl", "tr", "bl", "br"].includes(item?.snap) ? item.snap : "",
      width: Math.min(1800, Math.max(300, Number(item?.width) || 860)),
      height: Math.min(1200, Math.max(180, Number(item?.height) || 560)),
    })).filter((item) => item.windowId) : [],
    automationRules: Array.isArray(source.automationRules) ? source.automationRules.slice(0, 32).map((item) => ({
      id: text(item?.id, 80) || id("flow"),
      name: text(item?.name, 80) || "Neue Regel",
      enabled: bool(item?.enabled, true),
      trigger: {
        type: ["manual", "time", "online", "window-open"].includes(item?.trigger?.type) ? item.trigger.type : "manual",
        value: text(item?.trigger?.value, 80),
        time: time(item?.trigger?.time),
        days: days(item?.trigger?.days),
      },
      condition: {
        online: ["any", "online", "offline"].includes(item?.condition?.online) ? item.condition.online : "any",
        desktopId: text(item?.condition?.desktopId, 80),
      },
      actions: Array.isArray(item?.actions) ? item.actions.slice(0, 8).filter((action) => action && ["open", "layout", "desktop", "focus", "notify"].includes(action.type)).map((action) => ({
        id: text(action?.id, 80) || id("action"),
        type: action.type,
        value: text(action.value, 160),
      })) : [],
      lastRun: text(item?.lastRun, 32),
    })) : [],
    holosFiles: Array.isArray(source.holosFiles) ? source.holosFiles.slice(0, 60).map((item) => ({
      id: text(item?.id, 80) || id("file"),
      name: text(item?.name, 120) || "Neue Datei",
      folder: text(item?.folder, 120) || "Workspace",
      type: ["note", "link"].includes(item?.type) ? item.type : "note",
      content: text(item?.content, 6000),
      updatedAt: Number(item?.updatedAt) || Date.now(),
    })) : [],
    sharedBoards: Array.isArray(source.sharedBoards) ? source.sharedBoards.slice(0, 8).map((board) => ({
      id: text(board?.id, 80) || id("board"),
      name: text(board?.name, 80) || "Shared Workspace",
      roomCode: text(board?.roomCode, 32) || Math.random().toString(36).slice(2, 8).toUpperCase(),
      cards: Array.isArray(board?.cards) ? board.cards.slice(0, 40).map((card) => ({
        id: text(card?.id, 80) || id("card"),
        text: text(card?.text, 500),
        column: ["ideas", "doing", "done"].includes(card?.column) ? card.column : "ideas",
        updatedAt: Number(card?.updatedAt) || Date.now(),
      })).filter((card) => card.text) : [],
    })) : [],
    appBlueprints: Array.isArray(source.appBlueprints) ? source.appBlueprints.slice(0, 16).map((blueprint) => ({
      id: text(blueprint?.id, 80) || id("app"),
      name: text(blueprint?.name, 80) || "Meine Holos App",
      color: /^#[0-9a-f]{6}$/i.test(blueprint?.color) ? blueprint.color : "var(--feature-analysis)",
      blocks: Array.isArray(blueprint?.blocks) ? blueprint.blocks.slice(0, 12).map((block) => ({
        id: text(block?.id, 80) || id("block"),
        type: ["text", "clock", "links", "notes", "status"].includes(block?.type) ? block.type : "text",
        title: text(block?.title, 80) || "Block",
        value: text(block?.value, 1000),
      })) : [],
    })) : [],
    aiBudgets: Array.isArray(source.aiBudgets) ? source.aiBudgets.slice(0, 8).map((budget) => ({
      provider: text(budget?.provider, 40),
      monthly: Math.max(0, Math.min(100000, Number(budget?.monthly) || 0)),
    })).filter((budget) => budget.provider) : [],
  }
}

export function loadProductivity() {
  try {
    return normalizeProductivity(JSON.parse(localStorage.getItem(HOLOS_PRODUCTIVITY_KEY) ?? "{}"))
  } catch {
    return { ...PRODUCTIVITY_DEFAULTS }
  }
}

export function saveProductivity(value) {
  const normalized = normalizeProductivity(value)
  localStorage.setItem(HOLOS_PRODUCTIVITY_KEY, JSON.stringify(normalized))
  return normalized
}

export function newProductivityId(prefix) {
  return id(prefix)
}

export function emitHolosAction(type, value = "") {
  window.dispatchEvent(new CustomEvent(HOLOS_ACTION_EVENT, { detail: { type, value: String(value).slice(0, 120) } }))
}
